(function () {
  const SECTION_CLASS = "product-hotspots-section";
  const SWIPER_WAIT_STEP_MS = 100;
  const SWIPER_WAIT_MAX_MS = 4000;

  // Marker <-> card sync only; the Swiper itself is created by the shared
  // initSectionSliderLayout (global.js) via data-section-has-card-slider.
  // The active card advances by exactly one marker per prev/next click,
  // tracked here rather than read back from Swiper: with slidesPerView > 1,
  // the last couple of slides can never become Swiper's own leftmost/active
  // slide, so its reported index doesn't reliably match "which card should
  // be active" once you're near the end.
  class ProductHotspots {
    constructor(root) {
      this.root = root;
      this.markers = Array.from(
        root.querySelectorAll(".product-hotspots__marker")
      );
      this.slides = Array.from(
        root.querySelectorAll(".product-hotspots__slide")
      );
      this.overlays = Array.from(
        root.querySelectorAll(".product-hotspots__slide-overlay")
      );
      this.nextBtn = root.querySelector(".slider-nav-button--next");
      this.prevBtn = root.querySelector(".slider-nav-button--prev");
      this.paginationFill = this._setupPagination(root);
      this.sliderEl = root.querySelector(".js-section-card-slider");
      this.swiper = null;
      this.waitTimer = null;
      this.eventController = new AbortController();

      const activeOnLoad = this.markers.findIndex((marker) =>
        marker.classList.contains("is-active")
      );
      this.activeIndex = activeOnLoad === -1 ? 0 : activeOnLoad;

      this.markers.forEach((marker, index) => {
        marker.addEventListener("click", () => this._activate(index), {
          signal: this.eventController.signal,
        });
        // Tab-focusing a marker highlights it the same way a click does
        marker.addEventListener("focusin", () => this._activate(index), {
          signal: this.eventController.signal,
        });
      });

      // clicking an inactive card's overlay activates that hotspot
      this.overlays.forEach((overlay, index) => {
        overlay.addEventListener("click", () => this._activate(index), {
          signal: this.eventController.signal,
        });
      });

      // Tab-focusing any link/button inside a card activates its hotspot too
      this.slides.forEach((slide, index) => {
        slide.addEventListener("focusin", () => this._activate(index), {
          signal: this.eventController.signal,
        });
      });

      // Swiper's own navigation module is left disabled for this slider
      // (data-navigation="false") — these buttons step the active marker
      // instead of just scrolling, so we handle their clicks entirely here.
      if (this.nextBtn) {
        this.nextBtn.addEventListener(
          "click",
          () => this._activate(this.activeIndex + 1),
          { signal: this.eventController.signal }
        );
      }

      if (this.prevBtn) {
        this.prevBtn.addEventListener(
          "click",
          () => this._activate(this.activeIndex - 1),
          { signal: this.eventController.signal }
        );
      }

      this._updateNavButtons();
      this._updatePagination();
      this._waitForSwiper(0);
    }

    destroy() {
      this.eventController.abort();

      if (this.waitTimer) {
        clearTimeout(this.waitTimer);
        this.waitTimer = null;
      }

      this.swiper = null;
    }

    _waitForSwiper(elapsedMs) {
      const swiper = this.sliderEl ? this.sliderEl.swiper : null;

      if (swiper) {
        this.swiper = swiper;
        return;
      }

      if (elapsedMs >= SWIPER_WAIT_MAX_MS) return;

      this.waitTimer = setTimeout(() => {
        this._waitForSwiper(elapsedMs + SWIPER_WAIT_STEP_MS);
      }, SWIPER_WAIT_STEP_MS);
    }

    _activate(index) {
      const clampedIndex = Math.max(
        0,
        Math.min(index, this.markers.length - 1)
      );

      this._setActive(clampedIndex);

      if (this.swiper && !this.swiper.destroyed) {
        // Scrolls the target card into view even when Swiper can't make it
        // the leftmost slide (it settles as far as it can, which is still
        // enough to bring the card on screen).
        this.swiper.slideTo(clampedIndex);
      }
    }

    _setActive(index) {
      this.activeIndex = index;

      this.markers.forEach((marker, i) => {
        marker.classList.toggle("is-active", i === index);
      });
      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
      });

      this._updateNavButtons();
      this._updatePagination();
    }

    _updateNavButtons() {
      if (this.nextBtn) {
        this.nextBtn.classList.toggle(
          "swiper-button-disabled",
          this.activeIndex >= this.markers.length - 1
        );
      }

      if (this.prevBtn) {
        this.prevBtn.classList.toggle(
          "swiper-button-disabled",
          this.activeIndex <= 0
        );
      }
    }

    // Swiper's own progressbar pagination tracks scroll position, which
    // (like the nav buttons) doesn't line up with the active marker once
    // slidesPerView leaves some slides unreachable as the leftmost one — so
    // this fills one even step (1 / marker count) per active marker instead.
    _setupPagination(root) {
      const paginationEl = root.querySelector(".slider-pagination");
      if (!paginationEl) return null;

      paginationEl.classList.add(
        "swiper-pagination-progressbar",
        "swiper-pagination-horizontal"
      );

      let fill = paginationEl.querySelector(
        ".swiper-pagination-progressbar-fill"
      );
      if (!fill) {
        fill = document.createElement("span");
        fill.className = "swiper-pagination-progressbar-fill";
        paginationEl.appendChild(fill);
      }

      return fill;
    }

    _updatePagination() {
      if (!this.paginationFill || this.markers.length === 0) return;

      const progress = (this.activeIndex + 1) / this.markers.length;
      this.paginationFill.style.transform = `scaleX(${progress})`;
    }
  }

  function initSection(section) {
    if (!section || !section.classList?.contains(SECTION_CLASS)) return;

    const root = section.querySelector(".product-hotspots");
    if (!root) return;

    if (root._productHotspotsInstance) {
      root._productHotspotsInstance.destroy();
      root._productHotspotsInstance = null;
    }
    root._productHotspotsInstance = new ProductHotspots(root);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeProductHotspotsEvents) {
    window.__themeProductHotspotsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelectorAll(".product-hotspots").forEach((el) => {
        if (!el._productHotspotsInstance) return;

        el._productHotspotsInstance.destroy();
        el._productHotspotsInstance = null;
      });
    });
  }
})();
